<section class="create-user__layout">
    <!-- Sidebar stepper -->
    <div class="sidebar">
        <div class="sidebar-container">
            <div class="sidebar-logo">
                <img src="ntv-logo.png" alt="NTV Logo" />
            </div>

            <!-- Vertical Reverse Stepper -->
            <div class="stepper-container">
                <ntv-stepper
                    [steps]="createUserSteps()"
                    [currentStep]="currentStepIndex()"
                    [config]="stepperConfig()"
                    (stepClick)="handleStepClick($event)"
                    class="create-user-stepper"></ntv-stepper>
            </div>

            <div class="sidebar-button">
                <ntv-button
                    variant="secondary"
                    rounded="md"
                    size="xl"
                    (buttonClick)="backToUsers()">
                    <span class="px-2">Back to Users Page</span>
                </ntv-button>
            </div>
        </div>
    </div>

    <!-- Main content -->
    <main class="main" [class.main--role-picker]="currentStepId() === 'create-new-user'">
        <!-- Dynamic Header -->
        <div
            class="main__header"
            [class.main__header--role-picker]="currentStepId() === 'create-new-user'">
            <div>
                <h1>
                    {{
                        currentStepIndex() === 0
                            ? 'New User'
                            : 'New ' + (selectedUserType()?.label || 'User')
                    }}
                </h1>
                <p>
                    {{
                        selectedUserType()?.id === 'dealer'
                            ? 'Create a new account by filling out the information below'
                            : 'Create a new account by filling out the required information below'
                    }}
                </p>
            </div>
            @if (!isHiddenForNow()) {
                <app-button
                    (buttonClick)="showDraftsModal.set(true)"
                    label="My Drafts"
                    variant="accent"
                    size="xl"
                    [iconSize]="{
                        width: 14,
                        height: 14,
                    }"
                    iconKey="clock" />
            }
        </div>

        <div
            class="main__content"
            [class.custom-shadow]="currentStepId() !== 'create-new-user'"
            [class.main__content--role-picker]="currentStepId() === 'create-new-user'">
            @if (submitError()) {
                <div class="main__alert main__alert--error main__alert--floating">
                    <svg class="main__alert-icon">
                        <use href="assets/icons/icons-sprite.svg#info-circle" />
                    </svg>
                    <span class="main__alert-message">{{ submitError() }}</span>
                    <button class="main__alert-close" (click)="submitError.set(null)" type="button">
                        <svg class="w-4 h-4">
                            <use href="assets/icons/icons-sprite.svg#close" />
                        </svg>
                    </button>
                </div>
            }
            @switch (currentStepId()) {
                @case ('create-new-user') {
                    <app-create-new-user></app-create-new-user>
                }
                @case ('input-information') {
                    <app-input-information></app-input-information>
                }
                @case ('select-dealer') {
                    <app-select-dealer></app-select-dealer>
                }
                @case ('preview-details') {
                    <app-preview-details></app-preview-details>
                }
            }
        </div>

        <!-- Previous and Next Navigation -->
        @if (currentStepIndex() > 0) {
            <div class="main__navigation-container">
                <div class="main__navigation">
                    <ntv-button
                        variant="primary"
                        size="xl"
                        (buttonClick)="goPrev()"
                        [disabled]="isDraftApiLoading() || isLoading()">
                        <span class="px-5">Previous</span>
                    </ntv-button>
                    <ntv-button
                        variant="accent"
                        size="xl"
                        [loading]="isDraftApiLoading() || isLoading()"
                        (buttonClick)="goNext()"
                        [disabled]="
                            isDraftApiLoading() ||
                            isLoading() ||
                            (currentStepIndex() === createUserSteps().length - 1 &&
                                !isConfirmationChecked())
                        ">
                        <span class="px-8">{{
                            currentStepIndex() === createUserSteps().length - 1
                                ? 'Create Account'
                                : 'Next'
                        }}</span>
                    </ntv-button>
                </div>
            </div>
        }
    </main>
</section>

@if (showDraftsModal()) {
    <ntv-modal
        [isVisible]="showDraftsModal()"
        (modalClose)="showDraftsModal.set(false)"
        [grayBackground]="false">
        <app-drafts (closeModal)="showDraftsModal.set(false)"></app-drafts>
    </ntv-modal>
}

@if (showBackToStepOneModal()) {
    <ntv-modal
        class="create-user__back-step-modal"
        [isVisible]="showBackToStepOneModal()"
        (modalClose)="cancelBackToStepOneNavigation()"
        [grayBackground]="true">
        <div class="create-user__confirm-modal">
            <h3 class="create-user__confirm-title">Go back to Step 1?</h3>
            <p class="create-user__confirm-message">
                Changes in your current progress may not be saved. Do you still want to continue?
            </p>

            <div class="create-user__confirm-actions">
                <ntv-button
                    variant="secondary"
                    size="lg"
                    (buttonClick)="cancelBackToStepOneNavigation()">
                    <span class="px-4">Cancel</span>
                </ntv-button>
                <ntv-button
                    variant="accent"
                    size="lg"
                    (buttonClick)="confirmBackToStepOneNavigation()">
                    <span class="px-4">Continue</span>
                </ntv-button>
            </div>
        </div>
    </ntv-modal>
}

<!-- Success creation and verification link modal -->
@if (showVerificationLinkModal()) {
    <ntv-modal
        class="create-user__verify-modal"
        [isVisible]="showVerificationLinkModal()"
        (modalClose)="closeVerificationLinkAndSuccessModal()"
        [grayBackground]="true">
        <div class="create-user__success-modal">
            <div class="create-user__success-mark" aria-hidden="true">
                <svg class="create-user__success-mark-svg">
                    <use href="assets/icons/icons-sprite.svg#success-mark" />
                </svg>
            </div>

            <h3 id="create-user-verify-title" class="create-user__success-title">
                Account created
            </h3>
            <p class="create-user__success-lead">A verification link was sent to</p>
            <p class="create-user__success-email">{{ verificationEmail() }}</p>
            <p class="create-user__success-hint">
                Check the inbox (and spam) for that address to finish setup.
            </p>

            <div class="create-user__success-actions">
                <ntv-button
                    variant="accent"
                    size="lg"
                    (buttonClick)="closeVerificationLinkAndSuccessModal()">
                    <span class="px-6">Got it</span>
                </ntv-button>
            </div>
        </div>
    </ntv-modal>
}
